import { useState, useEffect, useRef } from 'react';
import { Flex, TextInput, Button, View, Alert } from '@instructure/ui';
import { login } from '../../lib/api';
import useAppStore from '../../stores/app';

import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../config';
import { useAuth } from '../../context/AuthContext';
import { replacePathPlaceholders } from '../../helpers/utilities';
import PageLoader from '../../helpers/loader';

const LoginScreen = () => {
  const navigate = useNavigate();
  const { authenticate, loginScreenMessage } = useAppStore();
  const { check } = useAuth();
  const [pageLoaded, setPageLoaded] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertMessageType, setAlertMessageType] = useState('error');
  const usernameRef = useRef();
  const passwordRef = useRef();

  useEffect(() => {
    // if there is a login screen message, show it, then clear it...
    if (loginScreenMessage) {
      setAlertMessage(loginScreenMessage.message);
      setAlertMessageType(loginScreenMessage.type);
      useAppStore.getState().setLoginScreenMessage(null);
    }

    // see if the token was valid...
    const checkLoader = async () => {
      const checkResult = await check({ redirectOnFailure: false });

      if (checkResult.successful) {
        navigate(
          replacePathPlaceholders(ROUTES.ROOM, { roomSid: checkResult.roomSid })
        );
      } else {
        setPageLoaded(true);
        setAlertMessage('You could not be authenticated');
      }
    };

    // only run this if we are on the /vtc route, this means it is direct access from already being authenticated...
    if (location.pathname === '/vtc') {
      checkLoader();
    } else {
      setPageLoaded(true);
    }
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const loginResult = await login(
      usernameRef.current.value,
      passwordRef.current.value
    );

    if (loginResult.successful) {
      await authenticate(loginResult.data.authentication.token);
      navigate(ROUTES.ROOMS);
    } else {
      // TODO: Add error handling
      setAlertMessage('Your credentials could not be authenticated');
    }
  };

  return (
    <>
      {pageLoaded ? (
        <>
          <Flex
            as="div"
            justifyContent="center"
            alignItems="center"
            height="100vh"
            width="100vw"
            background="secondary"
          >
            <Flex.Item shouldShrink shouldGrow>
              <Flex
                withVisualDebug={false}
                wrap="wrap"
                width="400px"
                justifyItems="space-around"
                margin="0 auto"
              >
                <Flex.Item padding="small">
                  {alertMessage.length > 0 && (
                    <Alert
                      variant={alertMessageType}
                      renderCloseButtonLabel="Close"
                      margin="small"
                    >
                      {alertMessage}
                    </Alert>
                  )}
                  <View
                    as="div"
                    width="500px"
                    withVisualDebug={false}
                    padding="xx-large"
                    shadow="above"
                    borderRadius="medium"
                    borderWidth="small"
                    borderColor="primary"
                  >
                    <TextInput
                      id="username"
                      renderLabel="Username"
                      ref={usernameRef}
                    />

                    <TextInput
                      id="password"
                      renderLabel="Password"
                      type="password"
                      ref={passwordRef}
                    />

                    <Button
                      color="primary"
                      margin="medium none none none"
                      onClick={handleSubmit}
                    >
                      Login
                    </Button>
                  </View>
                </Flex.Item>
              </Flex>
            </Flex.Item>
          </Flex>
        </>
      ) : (
        <PageLoader text="Checking Authentication..." />
      )}
    </>
  );
};

export default LoginScreen;
